/**
 * @fileoverview `<Amount>` — formatage des montants XOF.
 *
 * Le système manipule des montants entiers uniquement : le franc CFA n'a pas
 * de sous-unité dans le contrat GNPC/Paymetrust. Ce composant centralise donc
 * l'affichage `2 000 000 F CFA` et empêche les décimales fantômes.
 *
 * @see Documents/Paymetrust_Spec_Technique_v1.pdf §2.4
 */

import { cn } from '../lib/utils.ts';

export interface AmountProps {
  /** Montant XOF entier. Les décimales sont arrondies par sécurité d'affichage. */
  value: number;
  /** Variante compacte utile dans les tableaux denses. */
  compact?: boolean;
  /** Masque le suffixe `F CFA` quand le contexte le répète déjà. */
  hideCurrency?: boolean;
  /** Classe additionnelle sur le span racine. */
  className?: string;
}

/**
 * Formate un montant XOF sans décimales.
 *
 * @param value - Montant XOF entier.
 * @param hideCurrency - Si `true`, retourne seulement la valeur groupée.
 * @returns Chaîne lisible en français, ex. `45 000 F CFA`.
 *
 * @example
 * ```ts
 * formatXof(45000); // "45 000 F CFA"
 * ```
 */
export function formatXof(value: number, hideCurrency = false): string {
  const rounded = Math.round(value);
  const formatted = rounded.toLocaleString('fr-FR');
  return hideCurrency ? formatted : `${formatted} F CFA`;
}

/**
 * Affichage de montant GNPC.
 *
 * @param props - Montant, options de compacité, masquage devise et classe CSS.
 * @returns Span typographié avec chiffres tabulaires.
 *
 * @example
 * ```tsx
 * <Amount value={2000000} />
 * ```
 */
export function Amount({
  value,
  compact = false,
  hideCurrency = false,
  className,
}: AmountProps): React.JSX.Element {
  return (
    <span
      className={cn(
        'text-quipux-navy-950 font-mono font-semibold tabular-nums',
        'whitespace-nowrap',
        compact ? 'text-sm' : 'text-base',
        className,
      )}
    >
      {formatXof(value, hideCurrency)}
    </span>
  );
}
